import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { EmployerLayout } from '../JobPostPricing/EmployerLayout';
import { AssistantPage } from '../Applications/copilot/AssistantPage';
import { getApiUrl } from '../../../config/api';
import { getCachedHiredJobs } from './hiredProfilesPrefetch';
import { Card, CardContent } from '@/Components/ui/card';
import { Button } from '@/Components/ui/button';
import { Badge } from '@/Components/ui/badge';
import { Input } from '@/Components/ui/input';
import { Skeleton } from '@/Components/ui/skeleton';
import {
  MapPin,
  CheckCircle2,
  Clock3,
  Users,
  FileText,
  Loader2,
  Search,
  List as ListIcon,
  LayoutGrid,
  CalendarCheck,
  Sparkles,
  ChevronRight,
  BriefcaseBusiness,
  X,
} from 'lucide-react';
import { cn } from '@/lib/utils';

const DATE_OPTIONS = { year: 'numeric', month: 'short', day: 'numeric' };
function formatDate(value) {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleDateString('en-US', DATE_OPTIONS);
}

// ── Job ordering: newest JD first (job ids are random, so sort by created_at).
function parseDate(value) {
  const d = value ? new Date(value) : null;
  return d && !Number.isNaN(d.getTime()) ? d : null;
}
function byNewestJob(a, b) {
  const da = parseDate(a.created);
  const db = parseDate(b.created);
  if (da && db) return db - da;
  if (da) return -1; // dated jobs before undated (e.g. deleted roles)
  if (db) return 1;
  return a.title.localeCompare(b.title);
}
const WEEK_MS = 7 * 24 * 60 * 60 * 1000;
function postedLabel(value) {
  const d = parseDate(value);
  if (!d) return null;
  const days = Math.floor((Date.now() - d.getTime()) / (24 * 60 * 60 * 1000));
  if (days <= 0) return 'Posted today';
  if (days === 1) return 'Posted yesterday';
  if (days < 7) return `Posted ${days}d ago`;
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return `Posted ${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', ...(sameYear ? {} : { year: 'numeric' }) })}`;
}
function isNewJob(value) {
  const d = parseDate(value);
  return Boolean(d) && Date.now() - d.getTime() < WEEK_MS;
}

function jobIsClosed(job) {
  return !['', 'active', 'open'].includes(String(job?.requirement_status || job?.status || '').trim().toLowerCase());
}

// "AMAN KUMAR" → "Aman Kumar"; mixed-case names are left as typed.
function displayName(value) {
  const name = String(value || '').trim();
  if (!name || name !== name.toUpperCase()) return name;
  return name.toLowerCase().replace(/\b\w/g, (letter) => letter.toUpperCase());
}

// The backend labels a hire whose job was deleted "Unknown Position".
function roleLabel(title) {
  const t = String(title || '').trim();
  return !t || t === 'Unknown Position' ? 'Role removed' : t;
}

// Lower-case and collapse whitespace so "  aman   kumar " matches "AMAN KUMAR".
function norm(value) {
  return String(value || '').toLowerCase().replace(/\s+/g, ' ').trim();
}

// Joining state of a hire:
//   joined    — joined_at recorded
//   awaiting  — hired through Offer & Onboarding, not yet marked Joined
//   untracked — hired through the older flow, which had no joining step (no
//               application link), so we can't claim they are still waiting
function joinState(candidate) {
  if (candidate.joined_at) return 'joined';
  if (candidate.application_id) return 'awaiting';
  return 'untracked';
}

function initialsFor(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  return (parts.length > 1 ? parts[0][0] + parts[parts.length - 1][0] : parts[0].slice(0, 2)).toUpperCase();
}

const AVATAR_COLORS = [
  'bg-sky-100 text-sky-700', 'bg-violet-100 text-violet-700',
  'bg-emerald-100 text-emerald-700', 'bg-amber-100 text-amber-700',
  'bg-indigo-100 text-indigo-700', 'bg-rose-100 text-rose-700',
  'bg-teal-100 text-teal-700',
];
function avatarColor(name) {
  const s = String(name || '');
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}
function Avatar({ name, size = 'md' }) {
  const dims = size === 'sm' ? 'h-9 w-9 text-xs' : 'h-10 w-10 text-sm';
  return (
    <span className={cn('flex shrink-0 items-center justify-center rounded-full font-semibold', dims, avatarColor(name))}>
      {initialsFor(name)}
    </span>
  );
}

function isRecent(candidate) {
  if (!candidate.hired_date) return false;
  const hired = new Date(candidate.hired_date);
  if (Number.isNaN(hired.getTime())) return false;
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - 30);
  return hired >= cutoff;
}

const BADGE_BASE = 'inline-flex items-center gap-1 rounded-full px-2.5 py-0 text-[12px] font-semibold';
function StatusBadge({ candidate }) {
  const state = joinState(candidate);
  if (state === 'joined') {
    return <Badge className={cn(BADGE_BASE, 'border-green-200 bg-green-50 text-green-800 hover:bg-green-50')}><CheckCircle2 className="h-3.5 w-3.5" />Joined</Badge>;
  }
  if (state === 'awaiting') {
    return <Badge className={cn(BADGE_BASE, 'border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-50')}><Clock3 className="h-3.5 w-3.5" />Awaiting joining</Badge>;
  }
  return (
    <Badge title="Hired before joining dates were tracked" className={cn(BADGE_BASE, 'border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-50')}>
      <BriefcaseBusiness className="h-3.5 w-3.5" />Hired
    </Badge>
  );
}

// A report is only worth opening when the candidate completed at least one
// test for the job. Only an explicit `false` hides it, so rows stay clickable
// against an older backend that doesn't send the flag yet.
function hasReport(candidate) {
  return candidate.has_test_data !== false;
}

// Keyboard + click handler props that make a row/card open the report.
function clickableProps(onOpen, disabled) {
  return {
    role: 'button',
    tabIndex: 0,
    'aria-disabled': disabled || undefined,
    onClick: () => { if (!disabled) onOpen(); },
    onKeyDown: (e) => {
      if (disabled) return;
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(); }
    },
  };
}

// One hired candidate as a compact directory row. No scores here — this is an
// onboarding/records view; clicking opens the full evaluation report.
function HiredRow({ candidate, showRole, onOpen, opening, disabled }) {
  const hired = formatDate(candidate.hired_date);
  const joined = formatDate(candidate.joined_at);
  const state = joinState(candidate);
  const canOpen = hasReport(candidate);
  const role = roleLabel(candidate.job_title);
  return (
    <div
      {...(canOpen ? clickableProps(onOpen, disabled) : {})}
      title={canOpen ? 'View evaluation report' : undefined}
      className={cn(
        'group flex items-center gap-3 border-t border-border px-3 py-3 first:border-t-0 transition-colors',
        canOpen && 'cursor-pointer hover:bg-muted/40 focus-visible:bg-muted/40 focus-visible:outline-none',
        canOpen && disabled && !opening && 'cursor-default opacity-60',
      )}
    >
      <Avatar name={candidate.candidate_name} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold text-foreground">{displayName(candidate.candidate_name) || '—'}</span>
          <StatusBadge candidate={candidate} />
        </div>
        <p className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground">
          {showRole && <span className={cn('font-medium', role === 'Role removed' ? 'italic text-slate-400' : 'text-foreground')}>{role}</span>}
          {showRole && <span>·</span>}
          <span className="inline-flex items-center"><MapPin className="mr-1 h-3 w-3" />{candidate.candidate_location || 'Location not specified'}</span>
          {hired && <span>· Hired {hired}</span>}
          {joined && <span>· Joined {joined}</span>}
          {state === 'untracked' && <span className="text-slate-400">· joining not tracked</span>}
        </p>
      </div>
      {canOpen ? (
        <span className="flex shrink-0 items-center gap-1 text-xs font-medium text-muted-foreground group-hover:text-foreground">
          {opening
            ? <><Loader2 className="h-3.5 w-3.5 animate-spin" />Opening…</>
            : <><span className="hidden sm:inline opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">View report</span><ChevronRight className="h-4 w-4" /></>}
        </span>
      ) : (
        <span className="shrink-0 text-xs text-slate-400">No test data</span>
      )}
    </div>
  );
}

function SkeletonRow() {
  return (
    <div className="flex items-center gap-3 border-t border-border px-3 py-3 first:border-t-0">
      <Skeleton className="h-10 w-10 rounded-full" />
      <div className="flex-1 space-y-2"><Skeleton className="h-4 w-40" /><Skeleton className="h-3 w-64" /></div>
    </div>
  );
}

// A search box with a clear (×) button; Esc clears it.
function SearchBox({ value, onChange, placeholder, inputRef, className, shortcut }) {
  return (
    <div className={cn('relative', className)}>
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      <Input
        ref={inputRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Escape' && value) { e.preventDefault(); onChange(''); } }}
        placeholder={placeholder}
        className={cn('pl-9', value || shortcut ? 'pr-9' : '')}
      />
      {value ? (
        <button type="button" onClick={() => { onChange(''); inputRef?.current?.focus(); }} aria-label="Clear search"
          className="absolute right-2 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground">
          <X className="h-3.5 w-3.5" />
        </button>
      ) : shortcut ? (
        <kbd className="pointer-events-none absolute right-2.5 top-1/2 hidden -translate-y-1/2 rounded border border-slate-200 bg-slate-50 px-1.5 text-[10px] font-medium text-slate-400 sm:block">{shortcut}</kbd>
      ) : null}
    </div>
  );
}

export const HiredProfiles = () => {
  const [jobs, setJobs] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [candidatesLoading, setCandidatesLoading] = useState(true);
  const [error, setError] = useState(null);

  const [view, setView] = useState('list'); // 'list' | 'board'
  const [selectedJob, setSelectedJob] = useState('all'); // 'all' | job_id
  const [quickFilter, setQuickFilter] = useState('all'); // all|await|joined|recent
  const [searchQuery, setSearchQuery] = useState('');
  const [jobQuery, setJobQuery] = useState('');
  const [openingKey, setOpeningKey] = useState(null); // row whose report is loading

  const searchRef = useRef(null);
  const navigate = useNavigate();

  // "/" jumps to the candidate search from anywhere on the page.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey) return;
      const tag = (e.target?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select' || e.target?.isContentEditable) return;
      e.preventDefault();
      searchRef.current?.focus();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Open the candidate's evaluation report. Same contract as the recruiter
  // card (CandidateCardImproved → /reports). The backend resolves the test IDs
  // from the job itself, so email + job_id is all a hire record needs.
  const openReport = async (candidate) => {
    const key = candidate.id || candidate.candidate_email;
    if (openingKey) return;
    if (!candidate.candidate_email || !candidate.job_id) {
      toast.error('This hire record is missing its candidate or job link, so the report cannot be opened.');
      return;
    }
    setOpeningKey(key);
    try {
      const response = await fetch(getApiUrl('/api/view_test_details_candidate'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ candidate_id: candidate.candidate_email, job_id: candidate.job_id }),
      });
      if (!response.ok) throw new Error('Could not load the evaluation report.');
      const testData = await response.json();
      const result = testData.candidate_result || {};
      const score = (value) => (value != null ? `${Math.round(value)}/100` : 'Not taken');
      navigate(`/reports?jobID=${candidate.job_id}`, {
        state: {
          candidate: {
            details: {
              ...candidate,
              ...result,
              name: result.name || result.full_name || displayName(candidate.candidate_name),
              email: result.email || candidate.candidate_email,
            },
            scores: [
              { label: 'Screening', score: score(result.screening_score) },
              { label: 'Personality', score: score(result.personality_score) },
              { label: 'Behavioural', score: score(result.behavioral_score) },
              { label: 'Coding', score: score(result.coding_score) },
              { label: 'Communication', score: score(result.communication_score) },
              { label: 'Aptitude', score: score(result.aptitude_score) },
            ],
            rank: result.rank || 0,
            total_results: result.total_candidates || 0,
            tests: {
              screening: result.screening_details || null,
              coding: result.coding_details || null,
              personality: result.personality_details || null,
              behavioral: result.behavioral_details || null,
              communication: result.communication_details || null,
              aptitude: result.aptitude_details || null,
            },
          },
          jobDetails: testData.job_details,
        },
      });
    } catch (err) {
      console.error('Error opening evaluation report:', err);
      toast.error(err.message || 'Could not load the evaluation report.');
    } finally {
      setOpeningKey(null);
    }
  };

  const getCompanyEmail = () => localStorage.getItem('employer_email');
  const getCompanyId = () => localStorage.getItem('companyAccountId');

  const fetchJobs = useCallback(async () => {
    const cached = getCachedHiredJobs();
    if (cached?.length) { setJobs(cached); setLoading(false); } else { setLoading(true); }
    try {
      const companyEmail = getCompanyEmail();
      const companyId = getCompanyId();
      if (!companyEmail) { setError('No employer email found. Please login again.'); setLoading(false); return; }
      const url = getApiUrl(`/api/get_jobs_with_hired_count?company_email=${encodeURIComponent(companyEmail)}${companyId ? `&company_id=${companyId}` : ''}`);
      const response = await fetch(url, { method: 'GET', headers: { 'Content-Type': 'application/json' }, credentials: 'include' });
      if (!response.ok) throw new Error('Failed to fetch jobs');
      const data = await response.json();
      if (data.success && data.jobs) setJobs(data.jobs);
    } catch (err) {
      console.error('Error fetching jobs:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch ALL hired candidates once so search + the job rail work across every
  // job (the API returns all for the company when job_id is omitted).
  const fetchAllCandidates = useCallback(async () => {
    setCandidatesLoading(true);
    try {
      const companyEmail = getCompanyEmail();
      const companyId = getCompanyId();
      if (!companyEmail) return;
      let url = getApiUrl(`/api/get_hired_candidates?company_email=${encodeURIComponent(companyEmail)}`);
      if (companyId) url += `&company_id=${companyId}`;
      const response = await fetch(url, { method: 'GET', headers: { 'Content-Type': 'application/json' }, credentials: 'include' });
      if (!response.ok) throw new Error('Failed to fetch hired candidates');
      const data = await response.json();
      if (data.success) setCandidates(data.hired_candidates || []);
    } catch (err) {
      console.error('Error fetching hired candidates:', err);
    } finally {
      setCandidatesLoading(false);
    }
  }, []);

  useEffect(() => { fetchJobs(); fetchAllCandidates(); }, [fetchJobs, fetchAllCandidates]);

  // Job rail: jobs that actually have hires, with their live count. Title and
  // location/closed come from the jobs feed; count is derived from candidates
  // so the rail always matches the list.
  const countByJob = useMemo(() => {
    const map = new Map();
    candidates.forEach((c) => {
      const id = String(c.job_id);
      map.set(id, (map.get(id) || 0) + 1);
    });
    return map;
  }, [candidates]);

  const railJobs = useMemo(() => {
    const seen = new Set();
    const list = [];
    jobs.forEach((j) => {
      const id = String(j.job_id);
      const count = countByJob.get(id) || 0;
      if (count > 0) { list.push({ id, title: roleLabel(j.job_title), location: j.location, closed: jobIsClosed(j), created: j.created_at, count }); seen.add(id); }
    });
    // Any job present on candidates but missing from the jobs feed (e.g. deleted).
    candidates.forEach((c) => {
      const id = String(c.job_id);
      if (!seen.has(id)) { seen.add(id); list.push({ id, title: roleLabel(c.job_title), location: null, closed: false, created: null, count: countByJob.get(id) || 0 }); }
    });
    return list.sort(byNewestJob);
  }, [jobs, candidates, countByJob]);

  const jq = norm(jobQuery);
  const filteredRailJobs = useMemo(
    () => (jq ? railJobs.filter((j) => norm(`${j.title} ${j.location || ''}`).includes(jq)) : railJobs),
    [railJobs, jq],
  );

  // Candidate search is global: while a query is typed it spans every job,
  // whatever is selected in the rail. Clearing it returns to the selected job.
  const q = norm(searchQuery);
  const searching = Boolean(q);
  const effectiveJob = searching ? 'all' : selectedJob;

  const scoped = useMemo(() => candidates.filter((c) => {
    if (effectiveJob !== 'all' && String(c.job_id) !== String(effectiveJob)) return false;
    if (q && ![displayName(c.candidate_name), c.candidate_email, roleLabel(c.job_title), c.candidate_location]
      .some((v) => norm(v).includes(q))) return false;
    return true;
  }), [candidates, effectiveJob, q]);

  const matchesFilter = (c) => {
    if (quickFilter === 'await') return joinState(c) === 'awaiting';
    if (quickFilter === 'joined') return joinState(c) === 'joined';
    if (quickFilter === 'recent') return isRecent(c);
    return true;
  };
  const visible = scoped.filter(matchesFilter);

  const counts = useMemo(() => ({
    all: scoped.length,
    await: scoped.filter((c) => joinState(c) === 'awaiting').length,
    joined: scoped.filter((c) => joinState(c) === 'joined').length,
    recent: scoped.filter(isRecent).length,
  }), [scoped]);

  const summary = useMemo(() => ({
    total: candidates.length,
    jobs: new Set(candidates.map((c) => String(c.job_id))).size,
    awaiting: candidates.filter((c) => joinState(c) === 'awaiting').length,
  }), [candidates]);

  const selectJob = (id) => { setSelectedJob(id); setSearchQuery(''); };
  const resetAll = () => { setSelectedJob('all'); setQuickFilter('all'); setSearchQuery(''); setJobQuery(''); };

  const selectedJobMeta = railJobs.find((j) => j.id === String(effectiveJob));
  const scopeTitle = effectiveJob === 'all' ? 'All jobs' : (selectedJobMeta?.title || 'Job');

  const VIEW_TABS = [
    { key: 'list', label: 'List', Icon: ListIcon },
    { key: 'board', label: 'Board', Icon: LayoutGrid },
  ];
  const CHIPS = [
    { key: 'all', label: 'All' },
    { key: 'await', label: 'Awaiting joining', Icon: Clock3 },
    { key: 'joined', label: 'Joined', Icon: CheckCircle2 },
    { key: 'recent', label: 'Recent 30d', Icon: Sparkles },
  ];

  const busyContent = candidatesLoading && candidates.length === 0;
  const showRole = effectiveJob === 'all';

  const renderList = () => {
    if (busyContent) {
      return <div className="rounded-xl border border-border">{[1, 2, 3, 4, 5].map((i) => <SkeletonRow key={i} />)}</div>;
    }
    if (visible.length === 0) {
      return (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-muted text-muted-foreground"><Users className="h-7 w-7" /></span>
            <div>
              <p className="font-semibold text-foreground">{searching ? `No one matches “${searchQuery.trim()}”` : 'No hired candidates match'}</p>
              <p className="mt-1 text-sm text-muted-foreground">
                {searching ? 'Search covers name, email, role and location across all jobs.' : 'Nothing in this view yet.'}
              </p>
            </div>
          </CardContent>
        </Card>
      );
    }
    return (
      <div className="rounded-xl border border-border">
        {visible.map((c) => {
          const key = c.id || c.candidate_email;
          return (
            <HiredRow
              key={key}
              candidate={c}
              showRole={showRole}
              onOpen={() => openReport(c)}
              opening={openingKey === key}
              disabled={Boolean(openingKey)}
            />
          );
        })}
      </div>
    );
  };

  const renderBoard = () => {
    const columns = [
      { key: 'awaiting', label: 'Awaiting joining', dot: 'bg-amber-500' },
      { key: 'joined', label: 'Joined', dot: 'bg-emerald-500' },
      { key: 'untracked', label: 'Hired · joining not tracked', dot: 'bg-slate-400' },
    ].map((col) => ({ ...col, items: visible.filter((c) => joinState(c) === col.key) }));
    return (
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        {columns.map((col) => (
          <div key={col.key} className="rounded-xl bg-slate-50 p-2">
            <div className="flex items-center gap-2 px-2 pb-2 pt-1.5">
              <span className={cn('h-2 w-2 shrink-0 rounded-full', col.dot)} />
              <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{col.label}</span>
              <span className="ml-auto rounded-full bg-white px-2 text-[11px] font-semibold text-slate-500">{col.items.length}</span>
            </div>
            <div className="max-h-[65vh] space-y-2 overflow-y-auto">
              {col.items.length === 0 ? (
                <p className="py-4 text-center text-[11px] text-slate-400">—</p>
              ) : col.items.map((c) => {
                const key = c.id || c.candidate_email;
                const opening = openingKey === key;
                const canOpen = hasReport(c);
                return (
                  <div
                    key={key}
                    {...(canOpen ? clickableProps(() => openReport(c), Boolean(openingKey)) : {})}
                    title={canOpen ? 'View evaluation report' : 'No test data'}
                    className={cn(
                      'rounded-lg border border-slate-200 bg-white p-2.5 transition',
                      canOpen && 'cursor-pointer hover:border-slate-300 hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300',
                    )}
                  >
                    <div className="flex items-center gap-2">
                      <Avatar name={c.candidate_name} size="sm" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-[13px] font-semibold text-foreground">{displayName(c.candidate_name)}</p>
                        <p className="truncate text-[11px] text-muted-foreground">{roleLabel(c.job_title)}</p>
                      </div>
                      {canOpen && (opening ? <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-muted-foreground" /> : <ChevronRight className="h-4 w-4 shrink-0 text-slate-300" />)}
                    </div>
                    <p className="mt-1.5 text-[11px] text-muted-foreground">
                      {col.key === 'joined'
                        ? <span className="inline-flex items-center text-emerald-700"><CalendarCheck className="mr-1 h-3 w-3" />Joined {formatDate(c.joined_at)}</span>
                        : <span className={cn('inline-flex items-center', col.key === 'awaiting' ? 'text-amber-700' : 'text-slate-500')}><Clock3 className="mr-1 h-3 w-3" />Hired {formatDate(c.hired_date) || '—'}</span>}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    );
  };

  if (error) {
    return (
      <EmployerLayout>
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center gap-3 py-16 text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-muted"><FileText className="h-8 w-8 text-muted-foreground" /></span>
            <div>
              <p className="text-lg font-semibold text-foreground">Something went wrong</p>
              <p className="mt-1 text-sm text-muted-foreground">{error}</p>
            </div>
            <Button onClick={fetchJobs}>Try again</Button>
          </CardContent>
        </Card>
      </EmployerLayout>
    );
  }

  return (
    <EmployerLayout>
      <AssistantPage page="hired-profiles" />
      <div className="w-full max-w-6xl mx-auto px-4 md:px-6 py-2 space-y-5">
        {/* Header — one slim summary line replaces the three KPI cards, whose
            numbers only repeated the filter chips below. */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-foreground">Hired Candidates</h1>
            <div className="mt-1 text-sm text-muted-foreground">
              {busyContent ? (
                <Skeleton className="h-4 w-72" />
              ) : (
                <>
                  <span className="font-semibold text-foreground">{summary.total}</span> hired across{' '}
                  <span className="font-semibold text-foreground">{summary.jobs}</span> job{summary.jobs !== 1 ? 's' : ''}
                  {' · '}
                  <span className={cn('font-semibold', summary.awaiting ? 'text-amber-700' : 'text-foreground')}>{summary.awaiting}</span> awaiting joining
                </>
              )}
            </div>
          </div>
          <div className="flex w-full items-center gap-2 sm:w-auto">
            <div className="inline-flex shrink-0 items-center gap-1 rounded-xl bg-slate-100 p-1">
              {VIEW_TABS.map(({ key, label, Icon }) => (
                <button key={key} type="button" onClick={() => setView(key)}
                  className={cn('inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition', view === key ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700')}>
                  <Icon className="h-4 w-4" />{label}
                </button>
              ))}
            </div>
            <SearchBox
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="Search candidates across all jobs…"
              inputRef={searchRef}
              shortcut="/"
              className="w-full sm:w-72"
            />
          </div>
        </div>

        {/* Mobile job selector */}
        <div className="lg:hidden">
          <select value={effectiveJob} onChange={(e) => selectJob(e.target.value)} className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm">
            <option value="all">All jobs ({candidates.length})</option>
            {railJobs.map((j) => <option key={j.id} value={j.id}>{j.title} ({j.count})</option>)}
          </select>
        </div>

        <div className="flex gap-5 items-start">
          {/* Left rail: jobs with hires, with its own search */}
          <aside className="hidden lg:block w-60 shrink-0 border-r border-slate-200 pr-4">
            <p className="px-2 pb-2 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
              Jobs <span className="font-normal normal-case tracking-normal text-slate-400">· newest first</span>
            </p>
            {railJobs.length > 5 && (
              <SearchBox value={jobQuery} onChange={setJobQuery} placeholder="Search jobs…" className="mb-2 [&_input]:h-8 [&_input]:text-xs" />
            )}
            <button type="button" onClick={() => selectJob('all')}
              className={cn('mb-1 w-full rounded-lg px-3 py-2 text-left transition', effectiveJob === 'all' ? 'bg-emerald-50' : 'hover:bg-slate-50')}>
              <div className="flex items-center justify-between">
                <span className="text-[13px] font-semibold text-slate-800">All jobs</span>
                <span className="text-xs text-slate-500">{candidates.length}</span>
              </div>
              <p className="text-[10px] text-slate-400">{searching ? 'Searching all jobs' : 'Everyone hired'}</p>
            </button>
            <div className="max-h-[60vh] space-y-0.5 overflow-y-auto">
              {filteredRailJobs.length === 0 ? (
                <p className="px-3 py-4 text-center text-xs text-slate-400">No jobs match “{jobQuery.trim()}”</p>
              ) : filteredRailJobs.map((j) => (
                <button key={j.id} type="button" onClick={() => selectJob(j.id)} title={j.title}
                  className={cn('w-full rounded-lg px-3 py-2 text-left transition', String(effectiveJob) === j.id ? 'bg-emerald-50' : 'hover:bg-slate-50')}>
                  <div className="flex items-start justify-between gap-2">
                    <span className={cn('line-clamp-2 text-[13px] font-medium leading-snug', j.title === 'Role removed' ? 'italic text-slate-400' : 'text-slate-700')}>{j.title}</span>
                    <span className="shrink-0 pt-0.5 text-xs text-slate-500">{j.count}</span>
                  </div>
                  <div className="mt-0.5 flex min-w-0 items-center gap-1.5 text-[10px]">
                    {postedLabel(j.created) && (
                      <span className={cn('shrink-0', isNewJob(j.created) ? 'font-semibold text-emerald-600' : 'text-slate-400')}>{postedLabel(j.created)}</span>
                    )}
                    {j.location && <span className="truncate text-slate-400">{postedLabel(j.created) ? '· ' : ''}{j.location}</span>}
                    {j.closed && <span className="shrink-0 rounded bg-slate-100 px-1.5 py-px text-[9px] font-semibold uppercase tracking-wide text-slate-500">Closed</span>}
                  </div>
                </button>
              ))}
            </div>
          </aside>

          {/* Content */}
          <div className="min-w-0 flex-1">
            {/* Filter chips */}
            <div className="mb-4 flex items-center gap-2 overflow-x-auto pb-1">
              {CHIPS.map(({ key, label, Icon }) => {
                const active = quickFilter === key;
                return (
                  <button key={key} type="button" onClick={() => setQuickFilter(key)}
                    className={cn('inline-flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition',
                      active ? 'border-emerald-300 bg-emerald-50 text-emerald-700' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50')}>
                    {Icon && <Icon className="h-3.5 w-3.5" />}{label}
                    <span className={cn('rounded-full px-1.5 text-[10px] font-semibold', active ? 'bg-white text-emerald-700' : 'bg-slate-100 text-slate-500')}>{counts[key]}</span>
                  </button>
                );
              })}
            </div>

            {/* Scope line */}
            <div className="mb-3 flex flex-wrap items-center gap-2 text-sm">
              {searching ? (
                <span className="text-muted-foreground">
                  <span className="font-semibold text-foreground">{visible.length}</span> result{visible.length !== 1 ? 's' : ''} for{' '}
                  <span className="font-semibold text-foreground">“{searchQuery.trim()}”</span> across all jobs
                </span>
              ) : (
                <>
                  <span className="font-semibold text-foreground">{scopeTitle}</span>
                  {selectedJobMeta?.closed && <Badge variant="outline" className="border-slate-300 bg-slate-100 text-slate-700">Role closed</Badge>}
                  <ChevronRight className="h-3.5 w-3.5 text-slate-300" />
                  <span className="text-muted-foreground">{candidatesLoading ? <span className="inline-flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" />Loading…</span> : `${visible.length} candidate${visible.length !== 1 ? 's' : ''}`}</span>
                </>
              )}
              {(selectedJob !== 'all' || quickFilter !== 'all' || searching || jobQuery) && (
                <button type="button" onClick={resetAll} className="ml-1 text-xs text-muted-foreground underline-offset-2 hover:underline">Reset</button>
              )}
            </div>

            {candidates.length === 0 && !candidatesLoading ? (
              <Card className="border-dashed">
                <CardContent className="flex flex-col items-center gap-3 py-14 text-center">
                  <span className="flex h-14 w-14 items-center justify-center rounded-full bg-muted text-muted-foreground"><Users className="h-7 w-7" /></span>
                  <div>
                    <p className="font-semibold text-foreground">No hired candidates yet</p>
                    <p className="mt-1 text-sm text-muted-foreground">Candidates confirmed as Hired in Offer &amp; Onboarding will appear here.</p>
                  </div>
                </CardContent>
              </Card>
            ) : (
              view === 'list' ? renderList() : renderBoard()
            )}
          </div>
        </div>
      </div>
    </EmployerLayout>
  );
};

export default HiredProfiles;
